/**
 * AdminLoadingState - Full-width loading state component for admin interfaces
 *
 * SESSION_315: Sprint 026C - Fix admin empty state display width
 *
 * Provides a consistent, desktop-friendly loading state that:
 * - Takes full container width (uses Paper, not Center)
 * - Centers loader visually while maintaining layout structure
 * - Matches AdminEmptyState styling for visual consistency
 */
import React from 'react';
import { Paper, Stack, Text, Loader } from '@mantine/core';

/**
 * Props for AdminLoadingState component
 */
export interface AdminLoadingStateProps {
  /** Loading message to display */
  message?: string;
  /** Optional minimum height (default: 200px) */
  minHeight?: number | string;
}

/**
 * AdminLoadingState Component
 *
 * A full-width loading indicator for admin review screens.
 * Uses Paper for container width stability, with centered content inside.
 */
export const AdminLoadingState: React.FC<AdminLoadingStateProps> = ({
  message = 'Loading...',
  minHeight = 200
}): React.ReactElement => {
  return (
    <Paper
      p="xl"
      radius="md"
      withBorder
      style={{
        borderColor: 'var(--mantine-color-gray-3)',
        backgroundColor: 'var(--mantine-color-gray-0)',
        minHeight: typeof minHeight === 'number' ? `${minHeight}px` : minHeight,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      <Stack gap="md" align="center">
        <Loader size="lg" color="blue" />
        <Text size="sm" c="dimmed">
          {message}
        </Text>
      </Stack>
    </Paper>
  );
};
